import {
    AfterContentInit,
    ChangeDetectorRef,
    ContentChild,
    ContentChildren,
    Directive,
    forwardRef,
    OnDestroy,
    QueryList,
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { MediaQueryToggleDirective, MQToggle } from './matchers/common/media-query-toggle.directive';
import { MediaQueryMatchService } from './media-query-match.service';
import { MediaQuerySwitchDefaultDirective } from './media-query.module';

/**
 * Enable toggling of content based on media queries.
 *
 * Currently, this will always only match (up to) one case defined
 * via `mediaQuerySwitchMin`, `mediaQuerySwitchExact` or `mediaQuerySwitchDefault`.
 *
 * Matches for larger media queries will take precedence over smaller ones.
 * Matches for exact MQs will take precedence over minimums, which in turn take
 * precedence over the default case (the default acts like a `default` in a normal switch-case).
 *
 * If there are multiple items with the same media query condition, only the first will be displayed.
 *
 * @example
 * ```html
 * <!-- In this example, we have 5 MQs: 1 to 5 -->
 * <ng-container oscMediaQuerySwitch>
 *   <!-- since we have no directive set to match MQ1, the default will be displayed -->
 *   <h1 *oscMediaQuerySwitchDefault>fallback/default</h1>
 *   <!-- from MQ2 on, show this -->
 *   <h1 *oscMediaQuerySwitchMin="'mq2'"></h1>
 *   <!-- from MQ3 on, show this (effectively makes the previous element MQ2-only) -->
 *   <h2 *oscMediaQuerySwitchMin="'mq3'"></h2>
 *   <!-- only for MQ4, show this -->
 *   <h3 *oscMediaQuerySwitchExact="'mq4'"></h3>
 *   <!-- since neither an exact nor a new minimum match element are defined for MQ5, it will show the h2 again -->
 * </ng-container>
 * ```
 *
 * @see {@link MediaQuerySwitchMinDirective}
 * @see {@link MediaQuerySwitchExactDirective}
 * @see {@link MediaQuerySwitchDefaultDirective}
 * @see {@link MediaQueryToggleDirective}
 */
@Directive({
    selector: '[oscMediaQuerySwitch]',
    standalone: false
})
export class MediaQuerySwitchDirective implements AfterContentInit, OnDestroy {
    @ContentChild(forwardRef(() => MediaQuerySwitchDefaultDirective))
    private fallback?: MediaQuerySwitchDefaultDirective;

    @ContentChildren(MediaQueryToggleDirective)
    private mqSwitchCases!: QueryList<MQToggle>;

    private matchObserver!: Subscription;

    public constructor(
        private changeDetector: ChangeDetectorRef,
        private mediaQueryMatchService: MediaQueryMatchService
    ) {}

    public ngAfterContentInit(): void {
        const contentChange = this.mqSwitchCases.changes.pipe(startWith(this.mqSwitchCases)) as Observable<
            QueryList<MQToggle>
        >;

        this.matchObserver = contentChange
            .pipe(
                map((cases) => cases.toArray()),
                switchMap((cases) =>
                    this.mediaQueryMatchService
                        .observeFirstMatch(cases)
                        .pipe(map((match): [MQToggle[], MQToggle | undefined] => [cases, match]))
                )
            )
            .subscribe(([cases, match]) => this.updateSwitchState(cases, match));
    }

    public ngOnDestroy(): void {
        this.matchObserver.unsubscribe();
    }

    private updateSwitchState(cases: MQToggle[], match: MQToggle | undefined): void {
        for (const item of cases) {
            item.setActive(item === match);
        }

        this.fallback?.setActive(!match);

        // trigger change detection for toggled elements
        this.changeDetector.detectChanges();
    }
}
